import React, { useRef, useState, useEffect } from "react";
import sidebarIcon from "../../assets/sidebar_icon.svg";
import searchIcon from "../../assets/search_icon.svg";
import newChatIcon from "../../assets/new_chat_icon.svg";
import dotsIcon from "../../assets/dots_icon.svg";
import { useDispatch } from "react-redux";
import { openSearchModal } from "../../redux/slices/searchModalSlice";
import { closeSidebar } from "../../redux/slices/sidebarSlice";
import { Link } from "react-router";
import { ChatSession } from "../../types/type";
import { Tooltip } from "react-tooltip";
import EditSessionIcon from "../../assets/edit_session_icon.svg";
import DeleteSessionIcon from "../../assets/delete_session_icon.svg";
import { useDeleteChatSession, useRenameChatSession } from "./api";
import { toast } from "react-toastify";

interface Props {
  chatSessions: ChatSession[];
}

export default function Sidebar({ chatSessions }: Props) {
  const dispatch = useDispatch();
  const [sessionModalOpen, setSessionModalOpen] = useState<number | undefined>(
    undefined
  );
  const [renameSession, setRenameSession] = useState<number | undefined>(
    undefined
  );
  const [newTitle, setNewTitle] = useState("");
  const renameSessionMutation = useRenameChatSession();
  const deleteSessionMutation = useDeleteChatSession();
  const modalRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const isOutsideModal =
        modalRef.current && !modalRef.current.contains(event.target as Node);
      const isOutsideRenameInput =
        renameSession &&
        renameInputRef.current &&
        !renameInputRef.current.contains(event.target as Node);

      if (sessionModalOpen && isOutsideModal && !renameSession) {
        setSessionModalOpen(undefined);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sessionModalOpen, renameSession]);

  const handleRenameSession = (sessionId: number, title: string) => {
    if (title.trim() === "") return;

    renameSessionMutation.mutate(
      { 
        sessionId,
        newTitle: title
      },
      {
        onSuccess: () => {
          toast.success("Chat name updated successfully", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
          });
          setRenameSession(undefined);
        },
        onError: (error: any) => {
          toast.error(error.response?.data?.message || "Failed to rename session");
        },
      }
    );
  };
  const handleDeleteSession = (sessionId: number) => {
    deleteSessionMutation.mutate(sessionId, {
      onSuccess: () => {
        toast.success("Chat deleted successfully", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
        });
        setSessionModalOpen(undefined);
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.message || "Failed to delete session"
        );
      },
    });
  };

  const handleRenameInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTitle(e.target.value);
  };

  const handleRenameKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    sessionId: number
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleRenameSession(sessionId, newTitle);
      setRenameSession(undefined);
    } else if (e.key === "Escape") {
      setRenameSession(undefined);
    }
  };

  return (
    <div className='bg-[#F5F5F5] w-1/2 h-[calc(100vh-5rem)] p-6 md:w-1/4'>
      <div>
        <div className='flex justify-between items-center'>
          <div>
            <img
              src={sidebarIcon}
              onClick={() => dispatch(closeSidebar())}
              alt='Icon'
              className='w-8 h-8 cursor-pointer'
              data-tooltip-id='close-sidebar-tooltip'
            />
            <Tooltip
              id='close-sidebar-tooltip'
              place='bottom'
              content='Close Sidebar'
            />
          </div>
          <div>
            <img
              src={searchIcon}
              onClick={() => dispatch(openSearchModal())}
              alt='Icon'
              className='w-8 h-8 cursor-pointer'
              data-tooltip-id='search-chats-tooltip'
            />
            <Tooltip
              id='search-chats-tooltip'
              place='bottom'
              content='Search Chats'
            />
          </div>
        </div>
        <div className='flex flex-col justify-center mt-7'>
          <Link to='/chatbot'>
            <div className='bg-[#DBEAFE] w-full h-14 flex justify-center items-center mb-10 rounded-lg hover:bg-[#BFD3FF] cursor-pointer'>
              <img src={newChatIcon} alt='Icon' className='w-5 h-5 mr-2' />
              <h2 className='text-[#0275D8] font-semibold'>New Chat</h2>
            </div>
          </Link>
          {chatSessions.map((session) => (
            <div key={session.session_id} className='relative'>
              {renameSession === session.session_id ? (
                <div className='bg-[#E0E0E0] w-full h-14 mb-4 p-4 flex items-center justify-between rounded-lg'>
                  <input
                    ref={renameInputRef}
                    type='text'
                    placeholder='title'
                    className='w-full bg-white px-2 py-1 rounded outline-none text-black'
                    defaultValue={session.title}
                    onChange={handleRenameInputChange}
                    onKeyDown={(e) =>
                      handleRenameKeyDown(e, session.session_id)
                    }
                    autoFocus
                    onFocus={(e) => {
                      e.target.select();
                      setNewTitle(session.title);
                    }}
                    onBlur={() => {
                      if (newTitle.trim() !== "") {
                        handleRenameSession(session.session_id, newTitle);
                      }
                      setRenameSession(undefined);
                    }}
                  />
                </div>
              ) : (
                <Link
                  to={`/chatbot/${session.session_id}`}
                  className='flex items-center'
                >
                  <div className='bg-[#E0E0E0] w-full h-14 mb-4 p-4 flex items-center justify-between rounded-lg hover:bg-[#9E9E9E] cursor-pointer'>
                    <h2 className='text-black mr-4'>{session.title}</h2>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSessionModalOpen(
                          sessionModalOpen === session.session_id
                            ? undefined
                            : session.session_id
                        );
                      }}
                    >
                      <img
                        src={dotsIcon}
                        alt='Icon'
                        className='w-5 h-5 ml-auto'
                      />
                    </button>
                  </div>
                </Link>
              )}

              {sessionModalOpen === session.session_id && (
                <div
                  ref={modalRef}
                  className='bg-white absolute -bottom-14 -right-20 rounded-md shadow-lg p-1 z-20'
                >
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setRenameSession(session.session_id);
                      setSessionModalOpen(undefined);
                    }}
                    className='flex rounded-md hover:bg-gray-100 cursor-pointer p-2'
                  >
                    <img
                      src={EditSessionIcon}
                      alt='Edit Icon'
                      className='w-5 h-5 cursor-pointer mx-2'
                    />
                    <h2 className='text-black'>Rename</h2>
                  </div>
                  <div
                    onClick={() => handleDeleteSession(session.session_id)}
                    className='flex rounded-md hover:bg-red-100 cursor-pointer p-2'
                  >
                    <img
                      src={DeleteSessionIcon}
                      alt='Delete Icon'
                      className='w-6 h-6 cursor-pointer mr-2 ml-1'
                    />
                    <h2 className='text-[#EC221F]'>Delete</h2>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
